import type { FC } from 'react';
import { Suspense, useEffect } from 'react';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools'; // are only included in bundles when process.env.NODE_ENV === 'development'
import '@utils/config';
import '@utils/logger';
import { Routing } from '@components/routing/Routing/Routing';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { BrowserRouter } from 'react-router-dom';
import { theme } from './theme';
import { queryClient } from './query-client';
import { Loader } from '@components/atoms';
import { config } from '@utils/config';
import { routes } from '@components/routing/Routing/Routing.config';
import { FallbackContainer } from '@/App.style.tsx';
import { useAuthStore } from '@stores/auth.store.ts';
import { useVerifyGoogleTokenMutation } from '@services/mutations/use-verify-google-token.mutation.ts';
import { logger } from '@utils/logger';

const Fallback = () => (
  <FallbackContainer>
    <Loader color='secondary' />
  </FallbackContainer>
);

interface Props {}

const WrappedApp: FC<Props> = () => {
  const { mutateAsync: verifyGoogleToken } = useVerifyGoogleTokenMutation();
  const { isUserVerified, setIsUserVerified, accessToken } = useAuthStore();

  useEffect(() => {
    if (!isUserVerified) {
      try {
        if (accessToken) {
          void verifyGoogleToken({ token: accessToken });
        } else {
          setIsUserVerified(true);
        }
      } catch (error) {
        logger.error('App bootstrap error', { error });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserVerified]);

  return <Suspense fallback={<Fallback />}>{isUserVerified ? <Routing routes={routes} /> : <Fallback />}</Suspense>;
};

export const App: FC = () => {
  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          {config.isDebug && <ReactQueryDevtools initialIsOpen={false} />}
          <WrappedApp />
        </QueryClientProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
};
